import {type ExtendedItemProps, ItemPicker} from '@github-ui/item-picker-base/ItemPicker'
import type {ColorName} from '@github-ui/use-named-color'
import {useCallback, useMemo, useState} from 'react'
import {graphql, readInlineData, useFragment, useRelayEnvironment} from 'react-relay'

import {LABELS} from '../../../constants/labels'
import {commitClearProjectItemFieldValueMutation} from '../../../mutations/clear-project-item-field-value'
import {commitUpdateProjectItemFieldValueMutation} from '../../../mutations/update-project-item-field-value'
import {IssueFieldMultiSelectValueTokens} from '../../fields/IssueFieldSingleSelectValueToken'
import type {MultiSelectFieldConfigFragment$key} from './__generated__/MultiSelectFieldConfigFragment.graphql'
import type {MultiSelectFieldFragment$key} from './__generated__/MultiSelectFieldFragment.graphql'
import type {
  MultiSelectFieldOptionFragment$data,
  MultiSelectFieldOptionFragment$key,
} from './__generated__/MultiSelectFieldOptionFragment.graphql'
import {FieldWrapper} from './FieldWrapper'
import {type BaseFieldProps, getTokenStyle} from './Shared'
import {SingleSelectLeadingVisual} from './SingleSelectToken'

// eslint-disable-next-line @github-ui/github-monorepo/restrict-relay-queries
const MultiSelectFieldOptionFragment = graphql`
  fragment MultiSelectFieldOptionFragment on ProjectV2MultiSelectFieldOption @inline {
    id
    name
    color
  }
`

// eslint-disable-next-line @github-ui/github-monorepo/restrict-relay-queries
const MultiSelectFieldConfigFragment = graphql`
  fragment MultiSelectFieldConfigFragment on ProjectV2MultiSelectField {
    id
    name
    multiSelectOptions {
      ...MultiSelectFieldOptionFragment
    }
  }
`

// eslint-disable-next-line @github-ui/github-monorepo/restrict-relay-queries
const MultiSelectFieldFragment = graphql`
  fragment MultiSelectFieldFragment on ProjectV2ItemFieldMultiSelectValue {
    id
    options {
      ...MultiSelectFieldOptionFragment
    }
  }
`

type MultiSelectFieldProps = BaseFieldProps<MultiSelectFieldConfigFragment$key, MultiSelectFieldFragment$key>

export function MultiSelectField({
  viewerCanUpdate,
  itemId,
  projectId,
  field,
  value,
  onIssueUpdate,
}: MultiSelectFieldProps) {
  const [showInput, setShowInput] = useState(false)
  const [filter, setFilter] = useState('')
  const environment = useRelayEnvironment()

  const fieldData = useFragment(MultiSelectFieldConfigFragment, field)
  const valueData = useFragment(MultiSelectFieldFragment, value)

  const fieldId = fieldData.id
  const fieldName = fieldData.name

  const allOptions = useMemo(() => {
    const options = fieldData.multiSelectOptions || []
    return options.map(option =>
      // eslint-disable-next-line no-restricted-syntax
      readInlineData<MultiSelectFieldOptionFragment$key>(MultiSelectFieldOptionFragment, option),
    )
  }, [fieldData.multiSelectOptions])

  const valueOptions = useMemo(() => {
    const options = valueData?.options || []
    return options.map(option =>
      // eslint-disable-next-line no-restricted-syntax
      readInlineData<MultiSelectFieldOptionFragment$key>(MultiSelectFieldOptionFragment, option),
    )
  }, [valueData?.options])

  const selectedOptionIds = useMemo(() => new Set(valueOptions.map(option => option.id)), [valueOptions])

  const selectedItems = useMemo(
    () => allOptions.filter(option => selectedOptionIds.has(option.id)),
    [allOptions, selectedOptionIds],
  )

  const items = useMemo(() => {
    if (!filter) return allOptions
    return allOptions.filter(option => option.name.toLowerCase().includes(filter.toLowerCase()))
  }, [allOptions, filter])

  const onSelectionChange = useCallback(
    (selection: MultiSelectFieldOptionFragment$data[]) => {
      if (selection.length === 0) {
        commitClearProjectItemFieldValueMutation({
          environment,
          input: {
            fieldId,
            itemId,
            projectId,
          },
          onCompleted: onIssueUpdate,
        })
      } else {
        commitUpdateProjectItemFieldValueMutation({
          environment,
          input: {
            fieldId,
            itemId,
            projectId,
            value: {multiSelectOptionIds: selection.map(option => option.id)},
          },
          onCompleted: onIssueUpdate,
        })
      }

      setShowInput(false)
    },
    [environment, fieldId, itemId, onIssueUpdate, projectId],
  )

  const filterItems = useCallback((valueText: string) => {
    setFilter(valueText)
  }, [])

  const convertToItemProps = useCallback(
    (option: MultiSelectFieldOptionFragment$data): ExtendedItemProps<MultiSelectFieldOptionFragment$data> => {
      return {
        id: option.id,
        text: option.name,
        leadingVisual: () => <SingleSelectLeadingVisual inputColor={option.color as ColorName} />,
        source: option,
      }
    },
    [],
  )

  const valueTokens =
    valueOptions.length > 0 ? (
      <IssueFieldMultiSelectValueTokens
        options={valueOptions.map(option => ({
          id: option.id,
          name: option.name,
          color: option.color,
        }))}
        getTooltipText={() => undefined}
        size="small"
        tokenClassName={getTokenStyle(fieldName)}
      />
    ) : null

  return (
    <>
      {!viewerCanUpdate ? (
        <FieldWrapper
          showInput={showInput}
          setShowInput={setShowInput}
          placeholder={LABELS.emptySections.noValue(fieldName)}
          canUpdate={false}
          name={fieldName}
          value={valueTokens}
        />
      ) : (
        <ItemPicker
          items={items}
          initialSelectedItems={selectedItems}
          filterItems={filterItems}
          getItemKey={option => option.id}
          convertToItemProps={convertToItemProps}
          placeholderText={LABELS.emptySections.singleSelectFilterPlaceholder}
          selectionVariant="multiple"
          onSelectionChange={onSelectionChange}
          renderAnchor={anchorProps => {
            return (
              <FieldWrapper
                showInput={showInput}
                setShowInput={setShowInput}
                placeholder={LABELS.emptySections.noValue(fieldName)}
                name={fieldName}
                anchorProps={{
                  ...anchorProps,
                  'aria-label': valueOptions.length > 0 ? `Edit ${fieldName}` : `Set ${fieldName}`,
                }}
                value={valueTokens}
              />
            )
          }}
          height="large"
          improvedNoMatchAccessibility={items.length === 0 && filter.length > 0}
          noMatchMessage={
            items.length === 0 && filter.length > 0
              ? {
                  title: 'No option found',
                  variant: 'empty',
                  body: 'Try searching with a different query for results.',
                }
              : undefined
          }
          cssAnchorPositioningSettings={{fallbackStrategy: 'opposite-side'}}
        />
      )}
    </>
  )
}
